/**
 * FRIDGE-57 — the approved taxonomy, as the site's trails read it.
 *
 * The owner's taxonomy packet (docs/seo/taxonomy-proposal-2026-10-17, approved
 * 10/20/2026 with every recommendation of the 10/19/2026 brief) places every
 * page in one tree with one parent:
 *
 *   Fridge.com
 *   ├─ Refrigerators · Freezers · Coolers & Beverage · Parts & Filters · Guides & Data · Local
 *   └─ Brands · About Us
 *
 * This module is that tree's catalog half as plain data, so a breadcrumb can be
 * built from it on any page:
 *   - the spine pages (Parts & Filters and Guides & Data are menu groups with no
 *     page of their own, so a trail never links them);
 *   - the spine of every canonical product family (lib/collections.ts);
 *   - the type nodes: the families whose collection was live, indexed and serving
 *     products when the packet measured it (10/17/2026), each a page every trail
 *     under it may link;
 *   - the families that narrow another type (Small Chest Freezers under Chest
 *     Freezers);
 *   - the retired family heads the redirect layer folds into a survivor.
 *
 * The rules (recommendations 1, 2 and 11 of the brief):
 *   1. a brand × type collection hangs under its type;
 *   2. a product's trail stops at its type, under its spine;
 *  11. the collection page is the type's node; a root guide page of the same
 *      type (/garage-refrigerators) is that node's child.
 *
 * Pure and dependency-free: no React, no next/*, no Supabase, no lib/collections.
 * lib/breadcrumbs-catalog.ts and lib/breadcrumbs-product.ts turn these slugs
 * into named crumbs. tests/taxonomy-trails-fridge-57.test.ts binds every list
 * here to the committed packet and to the redirect layer, so the code cannot
 * drift from the approved tree.
 */

export type SpineId = 'refrigerators' | 'freezers' | 'coolers' | 'parts-filters' | 'guides-data' | 'local' | 'brands' | 'about'

export interface SpinePage {
  name: string
  url: string
}

/** The spines that have a page. Parts & Filters and Guides & Data are menu groups only. */
export const SPINE_PAGES: Readonly<Partial<Record<SpineId, SpinePage>>> = {
  refrigerators: { name: 'Refrigerators', url: '/collections/refrigerators' },
  freezers: { name: 'Freezers', url: '/collections/freezers' },
  // Question 3: the spine is named Coolers & Beverage; its page is the specialty-appliances collection.
  coolers: { name: 'Coolers & Beverage', url: '/collections/specialty-appliances' },
  local: { name: 'Local Fridge Finder', url: '/local' },
  brands: { name: 'Brands', url: '/brands' },
  about: { name: 'About Us', url: '/about-us' },
}

/** The three collections that ARE a spine page. */
export const SPINE_COLLECTION_SLUGS: Readonly<Record<string, SpineId>> = {
  refrigerators: 'refrigerators',
  freezers: 'freezers',
  'specialty-appliances': 'coolers',
}

/**
 * Part type nodes under /parts (recommendation 13 of the brief, approved
 * 10/20/2026: "add an Ice-Maker Kits part type under Parts (FPN)"). A part type
 * with its own rail on the parts page is a node, and a part of that type hangs
 * under it: Fridge.com › Parts › Ice-Maker Kits › {kit}. The node's URL is the
 * rail's anchor on /parts, the page the census served as a crumb. The keys are
 * products.type values from lib/parts/fpn.ts TRUE_PART_TYPES (the test binds
 * them); gaskets, sensors and hardware keep Fridge.com › Parts › {part} until
 * each earns a rail of its own.
 */
export const PART_TYPE_NODES: Readonly<Record<string, SpinePage>> = {
  'ice-maker-kit': { name: 'Ice-Maker Kits', url: '/parts#ice-maker-kits' },
}

/** The node a part type hangs under, or null (case- and whitespace-tolerant, like lib/parts/fpn.ts). */
export function partTypeNode(type: string | null | undefined): SpinePage | null {
  if (typeof type !== 'string') return null
  return PART_TYPE_NODES[type.trim().toLowerCase()] ?? null
}

/**
 * The canonical product families (lib/collections.ts allCanonicalSlugs()) by spine.
 * Every family is listed; a test fails when lib/collections.ts gains one this map does not place.
 */
const FAMILIES_BY_SPINE: Readonly<Record<'refrigerators' | 'freezers' | 'coolers' | 'parts-filters', readonly string[]>> = {
  refrigerators: [
    'refrigerators', 'energy-star-refrigerators', 'french-door-refrigerators', 'side-by-side-refrigerators',
    'top-freezer-refrigerators', 'bottom-freezer-refrigerators', 'mini-fridges', 'compact-mini-fridges', 'office-refrigerators',
    'apartment-refrigerators', 'garage-refrigerators', 'quiet-refrigerators', 'freezerless-refrigerators',
    'energy-efficient-refrigerators', 'retro-fridges', 'dorm-refrigerators', 'undercounter-refrigerators',
    'built-in-refrigerators', 'portable-refrigerators', 'kimchi-refrigerators', 'outdoor-refrigerators', 'smart-fridges',
    'garage-ready-refrigerators', 'counter-depth-refrigerators', 'door-in-door-refrigerators', '4-door-refrigerators',
    'fingerprint-resistant-refrigerators', 'black-stainless-refrigerators', 'stainless-steel-refrigerators',
    'drawer-refrigerators', 'single-door-refrigerators', 'double-drawer-refrigerators', 'column-refrigerators',
    'humidity-control-refrigerators', 'gaming-fridges', 'commercial-refrigerators-and-freezers', 'commercial-refrigerators',
    'commercial-display-cases', 'commercial-undercounter', 'commercial-undercounter-refrigerators',
    'under-20-cu-ft-refrigerators', '20-to-24-cu-ft-refrigerators', '24-to-28-cu-ft-refrigerators',
    '28-cu-ft-and-up-refrigerators', '18-inch-wide-refrigerators', '20-inch-wide-refrigerators', '21-inch-wide-refrigerators',
    '22-inch-wide-refrigerators', '23-inch-wide-refrigerators', '24-inch-wide-refrigerators', '26-inch-wide-refrigerators',
    '27-inch-wide-refrigerators', '28-inch-wide-refrigerators', '29-inch-wide-refrigerators', '30-inch-wide-refrigerators',
    '31-inch-wide-refrigerators', '32-inch-wide-refrigerators', '33-inch-wide-refrigerators', '34-inch-wide-refrigerators',
    '35-inch-wide-refrigerators', '36-inch-wide-refrigerators', '42-inch-wide-refrigerators', '48-inch-wide-refrigerators',
  ],
  freezers: [
    'freezers', 'energy-star-freezers', 'energy-efficient-freezers', 'upright-freezers', 'chest-freezers', 'compact-freezers',
    'mini-freezers', 'office-freezers', 'deep-freezers', 'small-chest-freezers', 'undercounter-freezers', 'drawer-freezers',
    'double-drawer-freezers', 'outdoor-freezers', 'freezer-coolers', 'garage-freezers', 'built-in-freezers', 'column-freezers',
    'portable-freezers', 'quiet-freezers', 'retro-freezers', 'commercial-freezers', 'commercial-undercounter-freezers',
  ],
  coolers: [
    'specialty-appliances', 'energy-star-specialty-appliances', 'wine-coolers', 'wine-fridges', 'dual-zone-wine-coolers',
    'beverage-coolers', 'beverage-fridges', 'beverage-centers', 'beer-fridges', 'kegerators', 'ice-makers',
    'water-coolers', 'hot-and-cold-water-coolers', // FRIDGE-651: the node that replaced the retired water-dispensers head
    'under-20-bottle-wine-coolers', '20-to-40-bottle-wine-coolers', '40-to-60-bottle-wine-coolers',
    '60-to-100-bottle-wine-coolers', '100-bottle-and-up-wine-coolers', 'commercial-bar-coolers', 'back-bar-coolers',
    'commercial-beverage-coolers', 'commercial-kegerators',
  ],
  // Collection-side filter families; the filter catalog itself lives under /filters.
  'parts-filters': [
    'water-filters', 'air-filters', 'refrigerator-filters', 'condenser-filters', 'ice-machine-filters', 'inline-filters',
  ],
}

/** The spine of every canonical product family. */
export const FAMILY_SPINE: Readonly<Record<string, SpineId>> = Object.fromEntries(
  (Object.entries(FAMILIES_BY_SPINE) as Array<[SpineId, readonly string[]]>).flatMap(([spine, slugs]) => slugs.map((slug) => [slug, spine])),
)

/** Type families whose parent is another type, not the spine page (a strict narrowing). */
export const FAMILY_PARENT: Readonly<Record<string, string>> = {
  'small-chest-freezers': 'chest-freezers',
  'hot-and-cold-water-coolers': 'water-coolers', // FRIDGE-651
  'double-drawer-refrigerators': 'drawer-refrigerators',
  'dual-zone-wine-coolers': 'wine-fridges',
  'under-20-bottle-wine-coolers': 'wine-fridges', '20-to-40-bottle-wine-coolers': 'wine-fridges', '40-to-60-bottle-wine-coolers': 'wine-fridges',
  '60-to-100-bottle-wine-coolers': 'wine-fridges', '100-bottle-and-up-wine-coolers': 'wine-fridges',
  'commercial-display-cases': 'commercial-refrigerators', 'commercial-undercounter': 'commercial-refrigerators',
  'commercial-undercounter-refrigerators': 'commercial-refrigerators', 'commercial-undercounter-freezers': 'commercial-freezers',
}

/**
 * Retired family heads and the survivor the redirect layer sends them to in one
 * hop (lib/manual-redirects.csv → redirects.csv). A trail never links a retired
 * head: a product or hub that names one hangs under the survivor. The test binds
 * this map to the redirect layer in both directions.
 */
export const FOLDED_FAMILY_HEADS: Readonly<Record<string, string>> = {
  'wine-coolers': 'wine-fridges', // FRIDGE-402
  'beverage-fridges': 'beverage-coolers', // FRIDGE-1450
  'beverage-centers': 'beverage-coolers', // FRIDGE-1452
  'compact-mini-fridges': 'mini-fridges', // FRIDGE-1460
  'garage-ready-refrigerators': 'garage-refrigerators', // FRIDGE-402
  'compact-freezers': 'mini-freezers', // FRIDGE-1425
}

/**
 * Families with no page a trail may link: their collection was gated, empty or
 * absent in the packet's census (10/17/2026). With the spine collections and the
 * folded heads, every other family is a type node.
 */
const FAMILIES_WITHOUT_A_NODE: ReadonlySet<string> = new Set([
  'hot-and-cold-water-coolers', // FRIDGE-651: born visible_unindexed at the 3-product floor; a node once it indexes
  'kimchi-refrigerators', 'commercial-refrigerators-and-freezers', 'double-drawer-freezers', 'commercial-undercounter-freezers',
  'water-filters', 'refrigerator-filters', 'condenser-filters', 'ice-machine-filters', 'inline-filters',
])

/**
 * The type nodes of the approved tree: families whose collection was active,
 * indexed and serving products in the packet's census, so a trail may link them
 * (95 on 10/17/2026; the test binds the set to the packet).
 */
export const TYPE_NODE_SLUGS: ReadonlySet<string> = new Set(
  Object.keys(FAMILY_SPINE).filter((slug) => !SPINE_COLLECTION_SLUGS[slug] && !FOLDED_FAMILY_HEADS[slug] && !FAMILIES_WITHOUT_A_NODE.has(slug)),
)

/**
 * The collection node a family or collection slug resolves to: a spine
 * collection or a type node, after following one fold. Null when the slug is
 * neither (a family whose page is gated or retired without a survivor node).
 */
export function collectionNodeSlug(slug: string | null | undefined): string | null {
  if (!slug) return null
  const s = FOLDED_FAMILY_HEADS[slug] ?? slug
  if (SPINE_COLLECTION_SLUGS[s] || TYPE_NODE_SLUGS.has(s)) return s
  return null
}

/** The spine a collection node belongs to. */
export function spineOfCollectionNode(nodeSlug: string): SpineId {
  return SPINE_COLLECTION_SLUGS[nodeSlug] ?? FAMILY_SPINE[nodeSlug] ?? 'refrigerators'
}

/**
 * One level of a trail above the leaf, before names are attached: a spine page
 * (named by the tree) or a collection node (named by lib/collections).
 */
export type TaxonomyLevel = { kind: 'spine'; spine: SpineId; name: string; url: string } | { kind: 'collection'; slug: string }

/** The spine page as a trail level, or null for a menu group with no page. */
export function spineLevel(spine: SpineId): TaxonomyLevel | null {
  const page = SPINE_PAGES[spine]
  return page ? { kind: 'spine', spine, name: page.name, url: page.url } : null
}

/**
 * Every linked level of a collection node's own trail, root first, INCLUDING the
 * node itself: the spine page (when it has one), the parent type (when the node
 * narrows one) and the node. A spine collection is just its spine page.
 */
export function collectionNodeLevels(nodeSlug: string): TaxonomyLevel[] {
  const spineId = SPINE_COLLECTION_SLUGS[nodeSlug]
  if (spineId) {
    const level = spineLevel(spineId)
    return level ? [level] : []
  }
  const levels: TaxonomyLevel[] = []
  const spine = spineLevel(spineOfCollectionNode(nodeSlug))
  if (spine) levels.push(spine)
  const parent = FAMILY_PARENT[nodeSlug]
  if (parent && TYPE_NODE_SLUGS.has(parent)) levels.push({ kind: 'collection', slug: parent })
  levels.push({ kind: 'collection', slug: nodeSlug })
  return levels
}
